import { ApiError } from "../../lib/api/client";
import type { SyncCounts } from "../../lib/api/models";

export const ZERO_COUNTS: SyncCounts = {
  clientsCreated: 0,
  clientsUpdated: 0,
  clientsHidden: 0,
  clientsShown: 0,
  sitesCreated: 0,
  sitesUpdated: 0,
  sitesDeleted: 0,
  itemsCreated: 0,
  itemsUpdated: 0,
  itemsMoved: 0,
  itemsDeleted: 0,
  skipped: 0,
};

const some = (n: number, one: string, many: string): string | null => (n > 0 ? `${n} ${n === 1 ? one : many}` : null);

/** What a pass changed, in plain words: "2 new clients, 14 assets updated". Empty when it changed nothing. */
export function syncChanges(counts: Partial<SyncCounts>): string {
  const c = { ...ZERO_COUNTS, ...counts };
  return [
    some(c.clientsCreated, "new client", "new clients"),
    some(c.clientsUpdated, "client renamed", "clients renamed"),
    some(c.clientsShown, "client shown again", "clients shown again"),
    some(c.clientsHidden, "client hidden", "clients hidden"),
    some(c.sitesCreated, "new site", "new sites"),
    some(c.sitesUpdated, "site updated", "sites updated"),
    some(c.sitesDeleted, "site removed", "sites removed"),
    some(c.itemsCreated, "new asset", "new assets"),
    some(c.itemsUpdated + c.itemsMoved, "asset updated", "assets updated"),
    some(c.itemsDeleted, "asset removed", "assets removed"),
    some(c.skipped, "record skipped", "records skipped"),
  ]
    .filter((p): p is string => p !== null)
    .join(", ");
}

/** The toast after a sync: "Synced: 2 new clients, 14 assets updated." */
export function syncSummary(counts: SyncCounts): string {
  const changes = syncChanges(counts);
  return changes ? `Synced: ${changes}.` : "Synced. Nothing changed in Gorelo.";
}

/** Why a sync did not run through, for the toast. */
export function syncProblem(e: unknown): string {
  if (!(e instanceof ApiError)) return "The sync failed.";
  switch (e.code) {
    case "sync_running":
      return "A sync is already running. Try again in a minute.";
    case "gorelo_not_configured":
      return "Gorelo is not connected. An administrator sets the key on the server.";
    case "client_unlinked":
      return "This client is not linked to Gorelo.";
    case "safety_stop":
      return `${e.message} An administrator can apply the removals under Admin, Gorelo.`;
    case "gorelo_failed":
      return `The sync failed: ${e.message}`;
    default:
      return e.message;
  }
}
